import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { PlayingCard } from '../components/PlayingCard';
import { useGame } from '../hooks/useGame';
import { getSocket } from '../lib/socket';
import { useActiveGame } from '../hooks/useActiveGame';
import { useGameOptions } from '../hooks/useGameOptions';
import { GameOptionsPanel } from '../components/GameOptionsPanel';

export default function HomePage() {
  const nav = useNavigate();
  const { attach, lobby } = useGame();
  const refreshActive = useActiveGame(s => s.refresh);
  const opts = useGameOptions(s => s.opts);
  const setOpts = useGameOptions(s => s.set);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showOptions, setShowOptions] = useState(false);

  useEffect(() => attach(), [attach]);

  async function createGame() {
    setBusy(true); setErr(null);
    const sock = getSocket();
    sock.emit('game:create', opts, (res) => {
      setBusy(false);
      if (res.ok) { refreshActive(); nav(`/game/${res.data.gameId}`); }
      else setErr(res.error);
    });
  }

  async function quickPlayBot() {
    setBusy(true); setErr(null);
    const sock = getSocket();
    const botOpts = { ...opts, isPrivate: true, maxPlayers: 2 };
    sock.emit('game:create', botOpts, (res) => {
      if (!res.ok) { setBusy(false); setErr(res.error); return; }
      const gameId = res.data.gameId;
      sock.emit('game:addBot', { gameId }, (r2) => {
        if (!r2?.ok) { setBusy(false); setErr(r2?.error || 'Could not add bot'); return; }
        sock.emit('game:start', { gameId }, (r3) => {
          setBusy(false);
          if (r3?.ok) { refreshActive(); nav(`/game/${gameId}`); }
          else setErr(r3?.error || 'Could not start');
        });
      });
    });
  }

  async function joinGame() {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setBusy(true); setErr(null);
    const sock = getSocket();
    sock.emit('game:join', { code: c }, (res) => {
      setBusy(false);
      if (res.ok) { refreshActive(); nav(`/game/${res.data.gameId}`); }
      else setErr(res.error);
    });
  }

  return (
    <AppShell>
      {/* Hero — stacked cards illustration + tagline */}
      <section className="relative overflow-hidden rounded-3xl mb-4 px-5 py-7 sm:py-10 felt-table">
        <div className="felt-watermark hidden sm:block">
          <span style={{ top: 12, left: 16, fontSize: 56, opacity: 0.5 }}>♠</span>
          <span style={{ bottom: 12, right: 16, fontSize: 56, opacity: 0.5, transform: 'rotate(180deg)' }}>♥</span>
        </div>
        <div className="relative flex items-center gap-4">
          {/* Real in-game cards as the hero illustration */}
          <div className="relative shrink-0" style={{ width: 110, height: 130 }} aria-hidden>
            <div className="absolute left-0 top-2" style={{ transform: 'rotate(-12deg)' }}>
              <PlayingCard card={{ suit: 'hearts', rank: 'Q', id: 'hero-qh' }} />
            </div>
            <div className="absolute left-8 top-5" style={{ transform: 'rotate(10deg)' }}>
              <PlayingCard card={{ suit: 'spades', rank: 'K', id: 'hero-ks' }} />
            </div>
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-wide text-accent leading-tight">Play Durak</h1>
            <p className="text-ink-100 text-sm sm:text-base">Outwit your friends — last one holding cards is the fool.</p>
          </div>
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-4">
        <section className="panel p-6">
          <h2 className="section-label mb-2">Start a new game</h2>
          <p className="text-ink-200 text-sm mb-4">Create a room and share the link with friends.</p>
          <div className="space-y-3">
            <button onClick={createGame} disabled={busy} className="btn-primary w-full">
              Create room
            </button>
            <button onClick={() => setShowOptions(s => !s)} className="btn-ghost w-full">
              {showOptions ? 'Hide' : 'Customize'} game rules
            </button>
            {showOptions && (
              <div className="mt-2">
                <GameOptionsPanel value={opts} onChange={setOpts} />
              </div>
            )}
            <button
              onClick={() => quickPlayBot()}
              disabled={busy}
              className="btn-secondary w-full"
            >
              🤖 Quick play vs bot
            </button>
          </div>
        </section>

        <section className="panel p-6">
          <h2 className="section-label mb-2">Join a game</h2>
          <p className="text-ink-200 text-sm mb-4">Have a code from a friend? Enter it here.</p>
          <div className="flex gap-2">
            <input
              className="input uppercase tracking-widest text-center"
              maxLength={6}
              placeholder="ABCDEF"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
            />
            <button onClick={joinGame} disabled={busy || code.length < 4} className="btn-primary">Join</button>
          </div>
          {err && <div className="mt-3 text-rose-400 text-sm">{err}</div>}

          <div className="mt-6">
            <h3 className="text-sm uppercase tracking-wider text-ink-300 mb-2">Open rooms</h3>
            {lobby.length === 0
              ? <div className="text-ink-300 text-sm italic">No rooms waiting for players right now.</div>
              : (
                <ul className="divide-y divide-ink-700/60">
                  {lobby.map(r => (
                    <li key={r.id} className="flex items-center justify-between py-2">
                      <div>
                        <div className="font-semibold text-sm">{r.hostName}'s room</div>
                        <div className="text-xs text-ink-300">{r.playerCount}/{r.maxPlayers} • Code: <span className="font-mono text-accent">{r.code}</span></div>
                      </div>
                      <button
                        className="btn-secondary !py-1.5 !px-3"
                        onClick={() => { setCode(r.code); }}
                      >Use code</button>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </section>
      </div>
    </AppShell>
  );
}

